import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Phone, ArrowRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { CONTACT, SERVICES } from "@/data/content";

const Services = () => (
  <main data-testid="services-page" className="pt-20">
    {/* ——— EN-TÊTE ——— */}
    <section className="relative overflow-hidden circuit-bg grain py-24 sm:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(15,52,140,0.1),transparent_55%)]" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.28em] text-brand-blue"
        >
          <span className="h-px w-10 bg-brand-yellow/70" />
          Nos prestations
        </motion.p>
        <h1 data-testid="services-title" className="mt-8 font-display text-4xl sm:text-6xl lg:text-7xl font-black uppercase leading-[0.95] tracking-tight">
          <MaskedLines
            delay={0.3}
            lines={[
              <span key="a" className="text-ink">Le manifeste</span>,
              <span key="b" className="text-brand-red glow-red">du courant.</span>,
            ]}
          />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-700"
        >
          Huit chapitres, une même signature : des installations propres, sûres et durables,
          réalisées par les fondateurs eux-mêmes — du résidentiel au tertiaire.
        </motion.p>
      </div>
    </section>

    <Marquee items={["Neuf & Rénovation", "NF C 15-100", "Devis Gratuit", "Intervention Locale", "Conseil Direct"]} />

    {/* ——— CHAPITRES ——— */}
    <section className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24 sm:space-y-32">
        {SERVICES.map((s, i) => (
          <article
            key={s.id}
            data-testid={`service-chapter-${s.id}`}
            className="grid items-center gap-10 lg:grid-cols-12"
          >
            <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <Reveal>
                <div className="relative">
                  <p className="font-display text-7xl sm:text-8xl font-black text-stroke-red select-none" aria-hidden="true">
                    {s.num}
                  </p>
                  <h2 className="mt-2 font-display text-2xl sm:text-4xl font-bold tracking-tight text-ink">
                    {s.title}
                  </h2>
                  <p className="mt-3 text-sm font-mono uppercase tracking-[0.2em] text-brand-blue">{s.baseline}</p>
                </div>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="mt-6 text-base leading-relaxed text-slate-700">{s.description}</p>
              </Reveal>
              <Reveal delay={0.2}>
                <ul className="mt-8 space-y-3">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-700">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-red/15 border border-brand-red/40">
                        <Check className="h-3 w-3 text-brand-red" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.28}>
                <Link
                  to="/contact"
                  data-testid={`service-cta-${s.id}`}
                  className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-blue"
                >
                  Demander un devis
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </Link>
              </Reveal>
            </div>

            <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
              <Reveal delay={0.1}>
                <motion.div
                  whileHover={{ scale: 0.985 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200"
                >
                  <div className="absolute inset-0 z-10 rounded-2xl border border-transparent transition-colors duration-500 group-hover:border-brand-blue/50" aria-hidden="true" />
                  <img
                    src={s.img}
                    alt={s.title}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/80 via-transparent to-transparent" aria-hidden="true" />
                  <p className="absolute bottom-4 left-5 font-mono text-xs uppercase tracking-[0.25em] text-white/70">
                    Chapitre {s.num} — EG 6.1
                  </p>
                </motion.div>
              </Reveal>
            </div>
          </article>
        ))}
      </div>
    </section>

    {/* ——— CTA ——— */}
    <section className="relative overflow-hidden border-t border-slate-200 py-24 circuit-bg grain" data-testid="services-cta">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(15,52,140,0.07),transparent_60%)]" aria-hidden="true" />
      <div className="relative max-w-3xl mx-auto px-4 text-center">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-5xl font-black uppercase tracking-tight text-ink">
            Un chapitre <span className="text-brand-red glow-red">à écrire ensemble ?</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <a
            href={CONTACT.phoneHref}
            data-testid="services-cta-phone"
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-brand-blue px-8 py-4 text-lg font-bold text-white shadow-[0_10px_30px_rgba(15,52,140,0.35)] transition-all duration-300 hover:bg-[#0a2668] hover:-translate-y-0.5"
          >
            <Phone className="h-5 w-5" />
            {CONTACT.phone}
          </a>
        </Reveal>
      </div>
    </section>
  </main>
);

export default Services;
